import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImg from '../Assets/Logos/ibas_logo.jpeg';

// Helper: Format currency to LKR with commas and 2 decimals
const formatLKR = (val) => {
    if (val === null || val === undefined || isNaN(Number(val))) return '0.00';
    return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Helper: Format date cleanly (e.g. 15 Oct 2026)
const formatDate = (d) => {
    if (!d) return '-';
    try {
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return String(d);
        return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return String(d);
    }
};

export const generateInvoice = (paymentDetails) => {
    return new Promise((resolve, reject) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const buildPdf = (imgElement) => {
            try {
                const pageWidth = 210;
                const margin = 14;
                const rightX = pageWidth - margin; // 196

                // ==========================================
                // 1. HEADER SECTION (Logo & IBAS Contact)
                // ==========================================
                if (imgElement) {
                    doc.addImage(imgElement, 'JPEG', margin, 12, 42, 20); // x, y, width, height
                }

                doc.setFontSize(22);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(3, 36, 94); // #03245e
                doc.text("INVOICE", rightX, 20, { align: 'right' });

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text("Institute of Business and Accounting Studies (IBAS)", margin, 37);
                doc.text("Email: info@ibas.lk | Web: www.ibas.lk", margin, 42);

                // Invoice Metadata (Top Right)
                const invoiceNo = `INV-${paymentDetails.transactionId || paymentDetails.paymentId || Date.now()}`;
                
                let dateStr = paymentDetails.date;
                if (!dateStr && paymentDetails.paymentDate) {
                    dateStr = paymentDetails.paymentDate;
                } else if (!dateStr && paymentDetails.createdAt) {
                    dateStr = paymentDetails.createdAt;
                }
                const formattedDate = formatDate(dateStr || new Date());

                doc.setFontSize(9);
                doc.setTextColor(51, 65, 85);
                doc.text(`Invoice No: ${invoiceNo}`, rightX, 30, { align: 'right' });
                doc.text(`Invoice Date: ${formattedDate}`, rightX, 35, { align: 'right' });

                const payMethod = paymentDetails.paymentMethod || paymentDetails.method || 'Online';
                doc.text(`Payment Method: ${payMethod}`, rightX, 40, { align: 'right' });

                let payStatus = paymentDetails.paymentStatus || 'Completed';
                payStatus = payStatus.charAt(0).toUpperCase() + payStatus.slice(1);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(16, 149, 19); // Green
                doc.text(`Payment Status: ${payStatus}`, rightX, 45, { align: 'right' });

                // Top Divider Line
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.line(margin, 49, rightX, 49);

                // ==========================================
                // 2. BILLED TO & COURSE INFO
                // ==========================================
                const startInfoY = 55;

                // Left: Student Info
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(100, 116, 139);
                doc.text("BILLED TO:", margin, startInfoY);

                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(15, 23, 42);
                doc.text(`${paymentDetails.userName || paymentDetails.name || 'Student'}`, margin, startInfoY + 6);

                // Right: Course & Value Info
                const courseName = paymentDetails.courseName || paymentDetails.course || 'Course Payment';
                const coursePriceVal = Number(paymentDetails.coursePrice) || 0;
                const coursePromoVal = Number(paymentDetails.coursePromoPrice) || 0;
                const alumniDiscVal = Number(paymentDetails.alumniDiscount) || 0;

                let fullCourseValue = coursePriceVal;
                if (coursePromoVal > 0 && paymentDetails.planName?.includes('Promotional')) {
                    fullCourseValue = coursePromoVal;
                }

                // If coursePrice is not directly passed, derive from installments
                if (fullCourseValue <= 0 && Array.isArray(paymentDetails.installments) && paymentDetails.installments.length > 0) {
                    fullCourseValue = paymentDetails.installments.reduce((sum, inst) => sum + (Number(inst.amount) || 0), 0);
                }
                if (fullCourseValue <= 0) {
                    fullCourseValue = Number(paymentDetails.amount) || 0;
                }

                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(100, 116, 139);
                doc.text("ENROLLED COURSE & VALUE:", rightX, startInfoY, { align: 'right' });

                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(3, 36, 94);
                doc.text(courseName, rightX, startInfoY + 6, { align: 'right' });

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(71, 85, 105);
                let fullValueText = `Full Course Value: ${formatLKR(fullCourseValue)} LKR`;
                if (alumniDiscVal > 0 && paymentDetails.isAlumni) {
                    fullValueText += ` (Alumni Discount: ${formatLKR(alumniDiscVal)} LKR)`;
                }
                doc.text(fullValueText, rightX, startInfoY + 11, { align: 'right' });

                // Divider Line
                doc.setDrawColor(226, 232, 240);
                doc.line(margin, startInfoY + 16, rightX, startInfoY + 16);

                // ==========================================
                // 3. MAIN PAYMENT ITEM TABLE
                // ==========================================
                const amountPaid = Number(paymentDetails.amount) || 0;
                const isInstallment = Boolean(
                    (paymentDetails.installmentNumber && Number(paymentDetails.installmentNumber) > 0) ||
                    (paymentDetails.planName && paymentDetails.planName.toLowerCase().includes('installment'))
                );
                const currentInstNumber = Number(paymentDetails.installmentNumber) || 1;

                let totalInstCount = Number(paymentDetails.totalInstallments) || 0;
                if (totalInstCount <= 0 && Array.isArray(paymentDetails.installments)) {
                    totalInstCount = paymentDetails.installments.length;
                }
                if (totalInstCount <= 0 && isInstallment) {
                    totalInstCount = currentInstNumber;
                }

                const itemDescription = isInstallment
                    ? `${courseName} - Installment Payment (Installment ${currentInstNumber} of ${totalInstCount})`
                    : `${courseName} - Full Course Payment`;

                const planTitle = paymentDetails.planName || (isInstallment ? 'Installment Plan' : 'Full Payment');

                autoTable(doc, {
                    startY: startInfoY + 20,
                    head: [["Item Description", "Payment Plan", "Amount Paid (LKR)"]],
                    body: [
                        [itemDescription, planTitle, `${formatLKR(amountPaid)} LKR`]
                    ],
                    theme: 'striped',
                    headStyles: { 
                        fillColor: [3, 36, 94], 
                        textColor: [255, 255, 255], 
                        fontStyle: 'bold',
                        fontSize: 9,
                        cellPadding: 4
                    },
                    columnStyles: {
                        0: { cellWidth: 100 },
                        1: { cellWidth: 42 },
                        2: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
                    },
                    styles: { fontSize: 9, cellPadding: 4 }
                });

                let nextY = doc.lastAutoTable.finalY + 8;

                // =======================================================
                // 4. INSTALLMENT BREAKDOWN & SCHEDULE TABLE (If Applicable)
                // =======================================================
                let nextInstallmentInfo = null;
                let totalPaidToDate = amountPaid;
                let remainingBalance = Math.max(0, fullCourseValue - amountPaid);

                if (isInstallment && totalInstCount > 0) {
                    // Build full installment schedule items
                    const rawInstallments = Array.isArray(paymentDetails.installments) && paymentDetails.installments.length > 0
                        ? [...paymentDetails.installments].sort((a, b) => (Number(a.installmentNumber) || 0) - (Number(b.installmentNumber) || 0))
                        : [];

                    const scheduleRows = [];
                    let accumulatedPaid = 0;

                    for (let i = 1; i <= totalInstCount; i++) {
                        const existing = rawInstallments.find(item => Number(item.installmentNumber) === i);
                        const instAmount = existing ? Number(existing.amount) : (amountPaid || fullCourseValue / totalInstCount);
                        const instDueDate = existing?.dueDate ? formatDate(existing.dueDate) : '-';

                        let status = 'UPCOMING';
                        if (i < currentInstNumber) {
                            status = 'PAID';
                            accumulatedPaid += instAmount;
                        } else if (i === currentInstNumber) {
                            status = 'PAID (This Invoice)';
                            accumulatedPaid += instAmount;
                        } else if (i === currentInstNumber + 1) {
                            status = 'NEXT DUE';
                            nextInstallmentInfo = {
                                installmentNumber: i,
                                amount: instAmount,
                                dueDate: instDueDate
                            };
                        } else {
                            status = 'UPCOMING';
                        }

                        scheduleRows.push([
                            `#${i}`,
                            `Installment ${i}`,
                            instDueDate,
                            `${formatLKR(instAmount)} LKR`,
                            status
                        ]);
                    }

                    totalPaidToDate = accumulatedPaid;
                    remainingBalance = Math.max(0, fullCourseValue - totalPaidToDate);

                    // Section Heading
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(3, 36, 94);
                    doc.text("INSTALLMENT PAYMENT SCHEDULE & PROGRESS", margin, nextY);

                    autoTable(doc, {
                        startY: nextY + 3,
                        head: [["#", "Installment Plan", "Due Date", "Amount (LKR)", "Payment Status"]],
                        body: scheduleRows,
                        theme: 'grid',
                        headStyles: { 
                            fillColor: [71, 85, 105], 
                            textColor: [255, 255, 255], 
                            fontStyle: 'bold',
                            fontSize: 8.5,
                            cellPadding: 3
                        },
                        columnStyles: {
                            0: { cellWidth: 14, halign: 'center' },
                            1: { cellWidth: 50 },
                            2: { cellWidth: 38 },
                            3: { cellWidth: 40, halign: 'right' },
                            4: { cellWidth: 40, halign: 'center' }
                        },
                        styles: { fontSize: 8.5, cellPadding: 3 },
                        didParseCell: (data) => {
                            if (data.section === 'body') {
                                const rowIdx = data.row.index + 1;
                                // Highlight current installment row with subtle blue background
                                if (rowIdx === currentInstNumber) {
                                    data.cell.styles.fillColor = [238, 245, 255];
                                }
                                // Status column styling
                                if (data.column.index === 4) {
                                    const cellText = String(data.cell.raw);
                                    if (cellText.includes('PAID')) {
                                        data.cell.styles.textColor = [16, 149, 19]; // Green
                                        data.cell.styles.fontStyle = 'bold';
                                    } else if (cellText === 'NEXT DUE') {
                                        data.cell.styles.textColor = [217, 119, 6]; // Amber/Orange
                                        data.cell.styles.fontStyle = 'bold';
                                    } else {
                                        data.cell.styles.textColor = [100, 116, 139]; // Slate gray
                                    }
                                }
                            }
                        }
                    });

                    nextY = doc.lastAutoTable.finalY + 8;
                }

                // ==========================================
                // 5. FINANCIAL SUMMARY BLOCK
                // ==========================================
                // Ensure there is enough space on the page for summary + footer
                if (nextY > 230) {
                    doc.addPage();
                    nextY = 20;
                }

                const summaryBoxWidth = 110;
                const summaryBoxX = rightX - summaryBoxWidth;
                let currentSummaryY = nextY;

                // Outer border for summary card
                doc.setDrawColor(226, 232, 240);
                doc.setFillColor(248, 250, 252);

                const summaryLines = [
                    { label: "Standard Course Value:", value: `${formatLKR(fullCourseValue)} LKR`, isBold: false }
                ];

                if (paymentDetails.couponCode && Number(paymentDetails.discountAmount) > 0) {
                    summaryLines.push({ 
                        label: `Coupon (${paymentDetails.couponCode}):`, 
                        value: `- ${formatLKR(paymentDetails.discountAmount)} LKR`, 
                        isBold: true, 
                        color: [5, 150, 105] 
                    });
                }

                summaryLines.push({ 
                    label: "Amount Paid (This Invoice):", 
                    value: `${formatLKR(amountPaid)} LKR`, 
                    isBold: true, 
                    color: [3, 36, 94] 
                });

                if (isInstallment) {
                    summaryLines.push({ 
                        label: "Total Paid to Date:", 
                        value: `${formatLKR(totalPaidToDate)} LKR`, 
                        isBold: false 
                    });

                    if (nextInstallmentInfo) {
                        summaryLines.push({ 
                            label: `Next Installment (Inst. ${nextInstallmentInfo.installmentNumber}):`, 
                            value: `${formatLKR(nextInstallmentInfo.amount)} LKR (${nextInstallmentInfo.dueDate})`, 
                            isBold: true,
                            color: [217, 119, 6] // Orange/Amber
                        });
                    }

                    summaryLines.push({ 
                        label: "Remaining Balance:", 
                        value: `${formatLKR(remainingBalance)} LKR`, 
                        isBold: true,
                        color: remainingBalance > 0 ? [185, 28, 28] : [16, 149, 19]
                    });
                } else {
                    summaryLines.push({ 
                        label: "Remaining Balance:", 
                        value: "0.00 LKR", 
                        isBold: true,
                        color: [16, 149, 19]
                    });
                }

                const boxHeight = (summaryLines.length * 6) + 8;
                doc.roundedRect(summaryBoxX, currentSummaryY, summaryBoxWidth, boxHeight, 2, 2, 'FD');

                let lineY = currentSummaryY + 6;
                doc.setFontSize(9);

                summaryLines.forEach((line) => {
                    doc.setFont('helvetica', line.isBold ? 'bold' : 'normal');
                    doc.setTextColor(71, 85, 105);
                    doc.text(line.label, summaryBoxX + 4, lineY);

                    if (line.color) {
                        doc.setTextColor(line.color[0], line.color[1], line.color[2]);
                    } else if (line.isBold) {
                        doc.setTextColor(15, 23, 42);
                    }
                    doc.text(line.value, rightX - 4, lineY, { align: 'right' });

                    lineY += 6;
                });

                // ==========================================
                // 6. FOOTER
                // ==========================================
                const footerY = 278;
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.line(margin, footerY - 6, rightX, footerY - 6);

                doc.setFontSize(8.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text("Thank you for choosing Institute of Business and Accounting Studies (IBAS)!", 105, footerY - 1, { align: 'center' });
                doc.text("This is an official computer-generated invoice and does not require a physical signature.", 105, footerY + 3.5, { align: 'center' });
                doc.text("For any billing inquiries, please contact: info@ibas.lk | +94 11 234 5678", 105, footerY + 8, { align: 'center' });

                // Save Document
                doc.save(`${invoiceNo}.pdf`);
                resolve(true);
            } catch (error) {
                console.error("Error generating invoice:", error);
                reject(error);
            }
        };

        const img = new Image();
        img.src = logoImg;
        img.onload = () => {
            buildPdf(img);
        };
        img.onerror = () => {
            console.warn("Could not load logo for invoice. Generating without logo.");
            buildPdf(null);
        };
    });
};
